import {
    Component,
    ComponentInterface,
    Element,
    Prop,
    Host,
    h,
    Watch,
    Listen,
} from "@stencil/core";
import Popover from "../../utils/popover";
import "../../utils/recognizer/long-press";

export type TooltipPlacement =
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end";

/**
 * @slot (default) - Content that the tooltip describes.
 * @slot tip - The content to show as tip.
 */
@Component({
    tag: "uic-tooltip",
    styleUrl: "tooltip.scss",
})
export class Tooltip implements ComponentInterface {
    private timerId: ReturnType<typeof setTimeout>;
    private popover: Popover;
    private positioner: HTMLElement;
    private target: HTMLElement;

    private isActivatedByTouch = false;

    @Element() host: HTMLUicTooltipElement;

    /**
     * The placement of UI indicating the direction.
     */
    @Prop({ reflect: true }) placement: TooltipPlacement = "bottom-start";

    /**
     * The distance in pixels from which to offset the tooltip away from its tool.
     */
    @Prop() distance = 12;

    /**
     * Set to `true` to prevent the panel from being clipped when the component is placed inside a container with
     * `overflow: auto|scroll|hidden`.
     */
    @Prop() hoist: boolean = false;

    /**
     * If set to `true`, opens the tooltip. May be used programmatically to open the tooltip.
     */
    @Prop({ mutable: true, reflect: true }) open = false;

    /**
     * If set to `true` the user cannot interact with the tool and no tooltip is shown.
     */
    @Prop({ reflect: true }) disabled = false;

    /**
     * If set to `true` shows a tooltip indicating an error.
     */
    @Prop({ reflect: true }) error = false;

    /**
     * The message to show the user as tip.
     */
    @Prop() tip: string;

    /**
     * Determines wether the tooltip should be closed by a close button.
     *
     * The close button is always visible when setting `visible` and never visible with `hidden`.
     * Setting `auto` shows the button only if the user opens the tooltip using a long press.
     */
    @Prop() closeable: "auto" | "hidden" | "visible" = "auto";

    @Watch("placement")
    @Watch("distance")
    @Watch("hoist")
    syncOptions() {
        this.popover.setOptions({
            placement: this.placement,
            strategy: this.hoist ? "fixed" : "absolute",
        });
    }

    @Watch("open")
    onOpenChange(value: boolean) {
        if (value === true) {
            this.openTooltip();
        } else {
            this.closeTooltip();
        }
    }

    @Listen("uicLongPress")
    onLongPress() {
        document.addEventListener("click", this.onDocumentClick);

        this.isActivatedByTouch = true;
        this.open = true;
    }

    private openTooltip() {
        if (this.disabled) {
            return;
        }

        if (!this.popover) {
            this.initializePopover();
        }
    }

    private initializePopover() {
        if (!(this.target && this.positioner)) {
            return;
        }

        this.popover = new Popover(this.target, this.positioner, {
            placement: this.placement,
            strategy: this.hoist ? "fixed" : "absolute",
            offsetFunction: ({ placement, reference }) => {
                const invert =
                    placement === "bottom-end" || placement === "top-end"
                        ? -1
                        : 1;
                return [(invert * reference.width) / 2, this.distance];
            },
        });
    }

    private closeTooltip() {
        if (this.disabled) {
            return;
        }

        document.removeEventListener("click", this.onDocumentClick);
        this.host.removeEventListener("mouseleave", this.onMouseLeave);

        this.isActivatedByTouch = false;
        this.popover.destroy();
        this.popover = null;
    }

    private onMouseEnter() {
        if (this.open || !this.tip?.length) {
            return;
        }

        this.host.addEventListener("mouseleave", this.onMouseLeave);
        this.timerId = setTimeout(() => {
            this.open = true;
        }, 800);
    }

    private onMouseLeave() {
        if (this.disabled) {
            return;
        }

        this.open = false;
        clearTimeout(this.timerId);
    }

    private onMouseUp() {
        // on touchscreens mouseenter is triggered upon touch, but mouseleave is not.
        // so we prevent opening by clearing the timer.
        clearTimeout(this.timerId);
    }

    private onDocumentClick(event: Event) {
        // close tooltip only when another element is clicked
        const path = event.composedPath();
        if (!path.includes(this.host)) {
            this.open = false;
        }
    }

    private get isCloseable() {
        return (
            this.closeable === "visible" ||
            (this.closeable === "auto" && this.isActivatedByTouch)
        );
    }

    connectedCallback() {
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    componentDidLoad() {
        if (this.open === true) {
            this.openTooltip();
        }
    }

    disconnectedCallback() {
        // close to prevent visible tooltips on reconnect
        this.open = false;

        if (this.popover) {
            this.popover.destroy();
        }
    }

    render() {
        return (
            <Host>
                <div
                    data-long-press-delay={800}
                    onMouseEnter={this.onMouseEnter}
                    onMouseUp={this.onMouseUp}
                    ref={(el) => (this.target = el)}
                    class={this.tip?.length > 0 ? "uic-tooltip__over" : ""}
                >
                    <slot></slot>
                </div>
                <div
                    ref={(el) => (this.positioner = el)}
                    class="uic-tooltip__positioner"
                >
                    <div
                        class={{
                            "uic-tooltip__content": true,
                            "uic-tooltip__content--closeable": this.isCloseable,
                            "uic-tooltip__content--restricted-width": !!this
                                .tip,
                        }}
                    >
                        <slot name="tip">{this.tip}</slot>
                        {this.isCloseable && (
                            <uic-icon-button
                                onClick={() => (this.open = false)}
                                class="uic-tooltip__close-button"
                                icon="uic:cross"
                            ></uic-icon-button>
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
